/**
 * Roman → Devanagari transliteration (Nepali).
 *
 * A rule-based, ITRANS-flavoured phonetic converter that runs entirely in the
 * browser. It handles the common consonants, vowels, matras, conjuncts,
 * anusvara and halant well enough for casual typing input like `namaste` →
 * `नमस्ते`, `nepaal` → `नेपाल`, `pani` → `पानि`.
 */

// Consonants: Roman token -> Devanagari consonant (implicit 'a')
const CONSONANTS = [
    // 3-char first (greedy matching)
    ["kSh", "क्ष"], ["ksh", "क्ष"], ["gya", "ज्ञ"],
    ["chh", "छ"], ["Chh", "छ"],
    ["tra", "त्र"], ["shr", "श्र"], ["Shr", "श्र"],
    // 2-char
    ["kh", "ख"], ["Kh", "ख"],
    ["gh", "घ"], ["Gh", "घ"],
    ["ng", "ङ"], ["Ng", "ङ"],
    ["ch", "च"], ["Ch", "च"],
    ["jh", "झ"], ["Jh", "झ"],
    ["Th", "ठ"],
    ["Dh", "ढ"],
    ["th", "थ"],
    ["dh", "ध"],
    ["ph", "फ"],
    ["bh", "भ"],
    ["sh", "श"],
    ["Sh", "ष"],
    // 1-char
    ["k", "क"], ["g", "ग"], ["c", "च"], ["j", "ज"],
    ["T", "ट"], ["D", "ड"], ["N", "ण"],
    ["t", "त"], ["d", "द"], ["n", "न"],
    ["p", "प"], ["f", "फ"], ["b", "ब"], ["m", "म"],
    ["y", "य"], ["r", "र"], ["l", "ल"],
    ["v", "व"], ["w", "व"],
    ["s", "स"], ["h", "ह"],
];

// Independent vowels (used at word start or after another vowel)
const VOWELS_INDEP = [
    ["aa", "आ"], ["A", "आ"],
    ["ai", "ऐ"], ["au", "औ"],
    ["ii", "ई"], ["ee", "ई"], ["I", "ई"],
    ["uu", "ऊ"], ["oo", "ऊ"], ["U", "ऊ"],
    ["ri", "ऋ"], ["R", "ऋ"],
    ["a", "अ"], ["i", "इ"], ["u", "उ"],
    ["e", "ए"], ["o", "ओ"],
];

// Matras (used after a consonant)
const MATRAS = [
    ["aa", "ा"], ["A", "ा"],
    ["ai", "ै"], ["au", "ौ"],
    ["ii", "ी"], ["ee", "ी"], ["I", "ी"],
    ["uu", "ू"], ["oo", "ू"], ["U", "ू"],
    ["R", "ृ"],
    // "a" after a consonant = inherent (no matra visible)
    ["a", ""],
    ["i", "ि"], ["u", "ु"],
    ["e", "े"], ["o", "ो"],
];

// Numbers and punctuation
const DIGITS = {
    "0": "०", "1": "१", "2": "२", "3": "३", "4": "४",
    "5": "५", "6": "६", "7": "७", "8": "८", "9": "९",
};

const SPECIAL = {
    ".": "।", // Nepali danda
    "M": "ं", // anusvara
    "H": "ः", // visarga
    "~": "ँ", // chandrabindu
};

const HALANT = "्";

function matchToken(input, i, table) {
    for (const [rom, dev] of table) {
        if (input.startsWith(rom, i)) return { rom, dev, len: rom.length };
    }
    return null;
}

const exactOverrides = {
    rajdhani: "राजधानी",
    kathmandu: "काठमाडौं",
    nepal: "नेपाल",
    nepaal: "नेपाल",
    namaste: "नमस्ते",
    bazar: "बजार",
    desh: "देश",
    pani: "पानी",
    ghatana: "घटना",
    dhanyabad: "धन्यवाद",
    dhanyavad: "धन्यवाद",
    timi: "तिमी",
    hami: "हामी",
    ramro: "राम्रो",
    aama: "आमा",
    bhasa: "भाषा",
    tapai: "तपाईं",
    gaun: "गाउँ",
    kitab: "किताब",
    school: "स्कूल",
    chhu: "छु",
    sansar: "संसार",
};

export function transliterate(input) {
    if (!input) return "";
    return input
        .split(/([A-Za-z]+)/)
        .map((part) => {
            if (!/^[A-Za-z]+$/.test(part)) return convertWord(part);
            const lower = part.toLowerCase();
            if (Object.prototype.hasOwnProperty.call(exactOverrides, lower)) return exactOverrides[lower];
            return convertWord(part[0].toLowerCase() + part.slice(1));
        })
        .join("");
}

function convertWord(input) {
    let out = "";
    let i = 0;
    let lastWasConsonant = false; // did we just place a consonant without a matra?

    const closeConsonant = (nextCharIsWordBoundary) => {
        // At a word boundary a bare consonant keeps its inherent 'a' vowel
        // (like Nepali Unicode). Elsewhere (e.g. before another consonant)
        // it gets a halant to form a conjunct.
        if (lastWasConsonant && !nextCharIsWordBoundary) out += HALANT;
        lastWasConsonant = false;
    };

    while (i < input.length) {
        const ch = input[i];

        // Whitespace / newline preserved
        if (/\s/.test(ch)) {
            closeConsonant(true);
            out += ch;
            i++;
            continue;
        }

        // Digits
        if (DIGITS[ch]) {
            closeConsonant(true);
            out += DIGITS[ch];
            i++;
            continue;
        }

        // Special punctuation
        if (SPECIAL[ch]) {
            if (ch === "M" || ch === "H" || ch === "~") {
                out += SPECIAL[ch];
                lastWasConsonant = false;
            } else {
                closeConsonant(true);
                out += SPECIAL[ch];
            }
            i++;
            continue;
        }

        // If we just placed a consonant, first try to match a matra
        if (lastWasConsonant) {
            const m = matchToken(input, i, MATRAS);
            if (m) {
                out += m.dev;
                lastWasConsonant = false;
                i += m.len;
                continue;
            }
            // No matra follows: previous consonant + halant + new consonant
            const next = matchToken(input, i, CONSONANTS);
            if (next) {
                out += HALANT + next.dev;
                lastWasConsonant = true;
                i += next.len;
                continue;
            }
            // Something unknown: previous consonant keeps inherent 'a'
            lastWasConsonant = false;
        }

        // Try consonant
        const cons = matchToken(input, i, CONSONANTS);
        if (cons) {
            out += cons.dev;
            lastWasConsonant = true;
            i += cons.len;
            continue;
        }

        // Try independent vowel
        const vow = matchToken(input, i, VOWELS_INDEP);
        if (vow) {
            out += vow.dev;
            i += vow.len;
            continue;
        }

        // Unknown character — pass through unchanged (also acts as word boundary)
        if (lastWasConsonant) lastWasConsonant = false;
        out += ch;
        i++;
    }

    // End of input: a trailing consonant keeps its inherent 'a'
    // (no halant added — matches common Nepali Unicode conversion)
    return out;
}
